import styled, { keyframes } from 'styled-components';
import { COLORS } from '../../constants/colors';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  justify-content: center;
  padding-top: 15vh;
  animation: ${fadeIn} 0.15s ease-out;
`;

export const PaletteContainer = styled.div`
  width: 100%;
  max-width: 640px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: ${slideUp} 0.2s ease-out;
  height: fit-content;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-width: calc(100% - 32px);
    margin: 0 16px;
  }
`;

export const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${COLORS.border};
  gap: 12px;
`;

export const SearchIcon = styled.div`
  color: ${COLORS.textLight};
  font-size: 18px;
  display: flex;
  align-items: center;
`;

export const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: ${COLORS.textDark};
  background: transparent;
  
  &::placeholder {
    color: ${COLORS.textLight};
  }
`;

export const ShortcutHint = styled.span`
  font-size: 12px;
  color: ${COLORS.textLight};
  padding: 4px 8px;
  background: ${COLORS.backgroundLight};
  border-radius: 6px;
  border: 1px solid ${COLORS.border};
  font-family: monospace;
`;

export const FilterTabs = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 16px;
  border-bottom: 1px solid ${COLORS.border};
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FilterTab = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  
  background: ${props => props.$active ? COLORS.textDark : 'transparent'};
  color: ${props => props.$active ? 'white' : COLORS.textMedium};
  
  &:hover {
    background: ${props => props.$active ? COLORS.textDark : COLORS.backgroundLight};
  }
`;

export const FilterBadge = styled.span`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  background: ${props => props.$active ? 'rgba(255,255,255,0.2)' : COLORS.backgroundLight};
  color: ${props => props.$active ? 'white' : COLORS.textLight};
`;

export const ResultsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${COLORS.border};
    border-radius: 3px;
  }
`;

export const ResultSection = styled.div`
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${COLORS.textLight};
`;

export const ResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: ${props => props.$selected ? '8px' : '0'};
  margin: ${props => props.$selected ? '2px 8px' : '0'};
  
  background: ${props => props.$selected ? COLORS.textDark : 'transparent'};
  color: ${props => props.$selected ? 'white' : COLORS.textDark};
  
  &:hover {
    background: ${props => props.$selected ? COLORS.textDark : COLORS.backgroundLight};
  }
`;

export const ResultIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  
  background: ${props => {
    switch (props.$type) {
      case 'page': return '#e3f2fd';
      case 'action': return '#e8f5e9';
      case 'member': return '#fff3e0';
      case 'booking': return '#f3e5f5';
      case 'volunteer': return '#e0f2f1';
      case 'call': return '#fce4ec';
      case 'recent': return '#f5f5f5';
      case 'history': return '#eeeeee';
      case 'suggestion': return '#e8eaf6';
      default: return COLORS.backgroundLight;
    }
  }};
  
  color: ${props => {
    switch (props.$type) {
      case 'page': return '#1976d2';
      case 'action': return '#388e3c';
      case 'member': return '#f57c00';
      case 'booking': return '#7b1fa2';
      case 'volunteer': return '#00796b';
      case 'call': return '#c2185b';
      case 'recent': return '#616161';
      case 'history': return '#757575';
      case 'suggestion': return '#3f51b5';
      default: return COLORS.textMedium;
    }
  }};
`;

export const ResultContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ResultLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  mark {
    background: #fff59d;
    color: ${COLORS.textDark};
    padding: 0 2px;
    border-radius: 2px;
  }
`;

export const ResultSubtitle = styled.div`
  font-size: 12px;
  color: inherit;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

export const ResultArrow = styled.div`
  color: inherit;
  opacity: ${props => props.$visible ? 0.7 : 0};
  font-size: 12px;
  transition: opacity 0.1s ease;
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${COLORS.textLight};
  
  h4 {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.textMedium};
  }
  
  p {
    margin: 0;
    font-size: 13px;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 16px;
  border-top: 1px solid ${COLORS.border};
  background: ${COLORS.backgroundLight};
`;

export const FooterHint = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${COLORS.textLight};
  
  kbd {
    padding: 2px 6px;
    background: white;
    border: 1px solid ${COLORS.border};
    border-radius: 4px;
    font-family: monospace;
    font-size: 11px;
  }
`;

export const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: ${COLORS.textLight};
  font-size: 13px;
  gap: 8px;
  
  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid ${COLORS.border};
    border-top-color: ${COLORS.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const HistoryActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

export const HistoryAction = styled.button`
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${COLORS.textLight};
  font-size: 11px;
  cursor: pointer;
  border-radius: 4px;
  
  &:hover {
    background: ${COLORS.backgroundLight};
    color: ${COLORS.textMedium};
  }
`;
